import { useEffect, useState, memo } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import FormErrors from "components/formParts/formErrors"
import { useTranslation } from "react-i18next"
import { decodeFirebaseError } from "utils/firebaseHelpers"
import Callout from "components/callout/callout"
import { CustomInput, ProductWrite, InputMaxLength, FormStatus, Product, ProductAiSuggestion } from "types"
import CustomInputs from "features/storages/categories/customInputs/customInputs"
import { logger } from "utils/secureLogger"
import useProducts from "hooks/useProducts"
import Section from "components/layout/section"
import TextInput from "components/inputs/textInput"
import ToggleInput from "components/inputs/toggleInput"
import NumberInput from "components/inputs/numberInput"
import TextAreaInput from "components/inputs/textareaInput"
import MultipleFileInput from "components/inputs/multipleFileInput"
import useCategories from "hooks/useCategories"
import useLocations from "hooks/useLocations"
import useStorages from "hooks/useStorages"
import useStorageLimits from "hooks/useStorageLimits"
import CategorySelector from "components/inputs/categorySelector"
import LocationSelector from "components/inputs/locationSelector"
import { ReactComponent as AddIcon } from "assets/icons/add.svg"
import Button from "components/inputs/button"
import ButtonGroup from "components/buttons/buttonGroup"
import useLayout from "hooks/useLayout"
import Form from "components/forms/form"
import ProcessingButton from "components/inputs/processingButton"
import InputGroup from "@/components/inputs/InputGroup"
import UserSelector from "@/components/searchAndSelect/UserSelector"
import BarcodeListInput from "@/components/inputs/barcodeListInput"
import { ReactComponent as AutoAwesomeIcon } from "assets/icons/auto_awesome.svg"

interface CreateProductFormProps {
    storageId: string
}

const CreateProductForm: React.FC<CreateProductFormProps> = ({ storageId }) => {

    const { t } = useTranslation()
    const state = useLocation().state as { new: boolean, categoryId: string, locationId: string }

    // React hooks.
    const navigate = useNavigate()

    const [sku, setSku] = useState(``)
    const [name, setName] = useState(``)
    const [description, setDescription] = useState(``)
    const [unit, setUnit] = useState(``)
    const [allowNegativeInventory, setAllowNegativeInventory] = useState(false)
    const [lowStockWarning, setLowStockWarning] = useState<number | undefined>(undefined)
    const [files, setFiles] = useState<File[]>([])
    const [responsibleUserId, setResponsibleUserId] = useState<string>("")
    const [barcodes, setBarcodes] = useState<string[]>([])

    const [customInputs, setCustomInputs] = useState<CustomInput>({})

    // Form status.
    const [errors, setErrors] = useState<string[]>([]);

    // Layout hooks.
    const { addToast, showDialog } = useLayout()

    const { lastAddedSku, storage, isSharedAccessEnabled } = useStorages({ storageId })

    const {
        createProductAsync,
        isCreating,
        products
    } = useProducts({ storageId })

    const {
        allCategories,
        categoriesStatus
    } = useCategories({ storageId })

    const {
        allLocations,
        locationsStatus
    } = useLocations({ storageId })
    
    // Get product count for account limits check (per storage)
    const currentProductCount = storage?.counters?.productCounter ?? products?.length ?? 0
    const storagePlan = storage?.settings?.storagePlan
    const { canAddProduct, limits } = useStorageLimits({ 
        storagePlan,
        currentProductCount 
    })

    const [selectedCategories, setSelectedCategories] = useState<string[]>(state?.categoryId ? [state.categoryId] : [])
    const [selectedLocations, setSelectedLocations] = useState<string[]>(state?.locationId ? [state.locationId] : [])

    useEffect(() => {
        setSku(lastAddedSku ? String(lastAddedSku) : '1')
    }, [lastAddedSku])

    // Set default unit from storage settings
    useEffect(() => {
        if (storage?.settings?.defaultProductUnit && !unit) {
            setUnit(storage.settings.defaultProductUnit)
        }
        if (storage?.ownerIds?.length && responsibleUserId === "") {
            setResponsibleUserId(storage.ownerIds[0])
        }
    }, [storage, unit, responsibleUserId])

    // Handle submit.
    const handleSubmit = async (event: React.FormEvent, saveAsDraft: boolean = false) => {
        event.preventDefault()
        if (storageId === undefined) {
            return
        }

        if (!responsibleUserId) {
            setErrors([t('errorResponsibleUserRequired')])
            addToast({ text: t('errorResponsibleUserRequired'), colorScheme: 'red' })
            return
        }
        setErrors([] as string[])
        
        // Check product limit before submitting
        if (!canAddProduct) {
            setErrors([t('errorProductLimitReached')])
            addToast({ text: t('errorProductLimitReached'), colorScheme: 'red' })
            return
        }
        
        try {
            const productData = {
                ids: {
                    storageId,
                },
                sku,
                name,
                description,
                unit,
                categories: selectedCategories,
                locations: selectedLocations,
                stockSettings: {
                    allowNegativeInventory,
                    lowStockWarning,
                    responsibleUserId,
                },
                customInputs,
                isDeleted: false,
                isDraft: saveAsDraft,
                barcodes: barcodes.filter((code) => code.trim() !== ""),
                images: {
                    files,
                },
            } as ProductWrite
            const result = await createProductAsync(productData) as Product
            logger.info('Product creation successful', { productId: result.id }, 'CreateProduct')
            addToast({ text: t('toastProductCreated'), colorScheme: 'green' })
            navigate(`/storage/products/${result.id}`)
        } catch (error: unknown) {
            logger.error('Failed to create product', error)
            const errorMessage = decodeFirebaseError(error instanceof Error ? error.message : String(error))
            setErrors([errorMessage])
            addToast({ text: t('toastProductError'), colorScheme: 'red' })
        }
    }

    const applyAiSuggestion = (payload: { files: File[]; suggestion: ProductAiSuggestion }) => {
        const { files: suggestedFiles, suggestion } = payload
        if (suggestion.sku) setSku(suggestion.sku)
        if (suggestion.name) setName(suggestion.name)
        if (suggestion.description) setDescription(suggestion.description)
        if (suggestion.unit) setUnit(suggestion.unit)
        if (suggestion.barcodes?.length) setBarcodes(suggestion.barcodes)
        if (suggestion.categories?.length) setSelectedCategories(suggestion.categories)
        if (suggestion.locations?.length) setSelectedLocations(suggestion.locations)
        if (suggestion.stockSettings) {
            setAllowNegativeInventory(!!suggestion.stockSettings.allowNegativeInventory)
            setLowStockWarning(suggestion.stockSettings.lowStockWarning)
        }
        if (suggestion.customInputs) {
            setCustomInputs((previous) => ({
                ...previous,
                ...suggestion.customInputs,
            }))
        }
        if (suggestedFiles.length) {
            setFiles(suggestedFiles)
        }
    }
    const handleSkuChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSku(e.target.value)
    }

    // Handle name change.
    const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setName(e.target.value)
    }

    // Handle description change.
    const handleDescriptionChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setDescription(e.target.value)
    }

    // Handle unit change.
    const handleUnitChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setUnit(e.target.value)
    }

    // Handle allow negative inventory change.
    const handleAllowNegativeInventoryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setAllowNegativeInventory(e.target.checked)
    }

    // Handle low stock warning change.
    const handleLowStockWarningChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setLowStockWarning(Number(e.target.value) > 0 ? Number(e.target.value) : undefined)
    }

    // Handle errors and status.
    if (categoriesStatus === FormStatus.LOADING || locationsStatus === FormStatus.LOADING) return <Callout type={`neutral`} loadingIcon={true}>{t('loadingCategories')}</Callout>

    // Return the JSX.
    return (
        <>
            {!canAddProduct && (
                <Callout type="warning">
                    {t('errorProductLimitReached', { limit: limits.products })}
                </Callout>
            )}
            <Form
                className={`group`}
                onSubmit={handleSubmit}
                isLoading={isCreating}
            >
                <Section>
                    <Callout type="info" id="createProductInfo">
                        {t('createProductInfo', 'You can create this product as a draft or publish it immediately. Drafts can be edited and published later.')}
                    </Callout>
                    <ButtonGroup>
                        <Button wideOnMobile
                            type="button"
                            design="white"
                            onClick={() => showDialog('suggest_product_from_images', {
                                storageId,
                                onSuccess: applyAiSuggestion,
                            })}
                        >
                            <AutoAwesomeIcon />
                            <span>{t('buttonSuggestProductFromImages')}</span>
                        </Button>
                    </ButtonGroup>
                </Section>
                <Section title={t('productImages')}>
                    <MultipleFileInput
                        label=""
                        files={files}
                        existingImages={[]}
                        onFilesChange={setFiles}
                        onExistingImageDelete={() => {}}
                        onImageReorder={() => {}}
                        onFilesReorder={setFiles}
                    />
                </Section>
                <Section title={t('sectionProductDetails')}>
                    <InputGroup>
                        <TextInput label={t('labelSKU')} value={sku} required onChange={handleSkuChange} maxLength={InputMaxLength.short} />
                        <TextInput label={t('labelProductName')} value={name} required onChange={handleNameChange} maxLength={InputMaxLength.short} />
                    </InputGroup>
                    <TextAreaInput label={t('description')} value={description} onChange={handleDescriptionChange} maxLength={InputMaxLength.long} />
                    <TextInput label={t('unit')} value={unit} required onChange={handleUnitChange} maxLength={InputMaxLength.short} />
                </Section>
                <Section title={t('labelBarcodes')}>
                    <BarcodeListInput barcodes={barcodes} onChange={setBarcodes} />
                </Section>
                <Section title={t('sectionStock')}>
                    <InputGroup>
                        <ToggleInput label={t('allowNegativeInventory')} checked={allowNegativeInventory} onChange={handleAllowNegativeInventoryChange} />
                        <NumberInput label={t('lowStockWarning')} value={lowStockWarning || 0} onChange={handleLowStockWarningChange} />
                    </InputGroup>
                    {isSharedAccessEnabled() && <UserSelector
                        storageId={storageId}
                        value={responsibleUserId}
                        onChange={setResponsibleUserId}
                        label={t('labelResponsibleUser')}
                        required={true}
                    />}
                </Section>
                <Section title={t('sectionCategoryAndLocation')}>
                    <div className={`input-holder-wide`}>
                        <label>{t('category')}</label>
                        <CategorySelector
                            categories={allCategories}
                            allCategories={allCategories}
                            selectedCategories={selectedCategories}
                            setSelectedCategories={setSelectedCategories}
                        />
                    </div>
                    <div className={`input-holder-wide`}>
                        <label>{t('location')}</label>
                        <LocationSelector
                            locations={allLocations}
                            allLocations={allLocations}
                            selectedLocations={selectedLocations}
                            setSelectedLocations={setSelectedLocations}
                        />
                    </div>
                </Section>
                <CustomInputs storageId={storageId} categoryIds={selectedCategories} customInputs={customInputs} setCustomInputs={setCustomInputs} categories={allCategories} />
                <Section>
                    <ButtonGroup>
                        {isCreating ? (
                            <ProcessingButton design="primary" />
                        ) : (
                            <>
                                <Button wideOnMobile 
                                    type="button"
                                    design="white" 
                                    disabled={!canAddProduct}
                                    onClick={() => handleSubmit({ preventDefault: () => {} } as React.FormEvent, true)}
                                >
                                    <AddIcon />
                                    <span>{t('buttonCreateAsDraft', 'Create as Draft')}</span>
                                </Button>
                                <Button wideOnMobile 
                                    type="submit" 
                                    design="primary" 
                                    disabled={!canAddProduct}
                                >
                                    <AddIcon />
                                    <span>{t('buttonCreateProduct')}</span>
                                </Button>
                            </>
                        )}
                    </ButtonGroup>
                </Section>
                {errors.length > 0 && <FormErrors errors={errors} />}
            </Form>
        </>
    )

}

export default memo(CreateProductForm)



